import { api } from "../api/client.ts";
import type { Bookmark, BookmarkKind, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { aboutOf, bookmarkKey, bookmarkName, bookmarkPath, groupBookmarks, setBookmark } from "../lib/bookmarks.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import type { PageProps } from "../lib/state.ts";
import { IconButton, Link } from "../ui/Button.tsx";
import { CloseIcon, CommentIcon, KIND_ICONS, NAV_ICONS } from "../ui/icons.tsx";
import { Async, Badge, EmptyState } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";

/** Each kind's icon beside its bookmarks. */
const ICONS: Record<BookmarkKind, typeof CommentIcon> = {
  item: KIND_ICONS.record,
  conversation: NAV_ICONS.conversation,
  comment: CommentIcon,
  commit: NAV_ICONS.history,
};

/** What a bookmark is called on the page: its title now, or what stands for it. */
function nameOf(b: Bookmark): string {
  return b.available ? bookmarkName(b) : t("bookmarks.unavailable");
}

/** A comment's bookmark: who wrote it and how it starts (linking to it), and where it is. */
function CommentLine({ space, b }: { space: SpaceOut; b: Bookmark }) {
  if (!b.available) {
    return <span class="text-zinc-500 italic">{nameOf(b)}</span>;
  }
  const about = aboutOf(b);
  const where = about === "space" ? t("bookmarks.channel") : t("bookmarks.commentIn", { title: nameOf(b) });
  return (
    <span class="flex min-w-0 flex-col gap-0.5">
      <Link href={spaceUrl(space, bookmarkPath(b))} class={`${LINK} min-w-0 truncate`}>
        <span class="font-medium">{b.author_name}</span>
        {": "}
        {b.excerpt ?? t("bookmarks.noText")}
      </Link>
      <span class="flex min-w-0 items-center gap-2 text-xs text-zinc-500">
        <span class="truncate">{where}</span>
        {about === "space" ? null : <Badge>{t(`bookmarks.about.${about}`)}</Badge>}
      </span>
    </span>
  );
}

function Row({ space, b, onRemoved }: { space: SpaceOut; b: Bookmark; onRemoved: () => void }) {
  const Icon = ICONS[b.kind];
  const about = aboutOf(b);
  return (
    <li class="flex items-center gap-3 px-4 py-2.5 text-sm">
      <Icon class="shrink-0 text-zinc-500" />
      <span class="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
        {b.kind === "comment" ? (
          <CommentLine space={space} b={b} />
        ) : b.available ? (
          <Link href={spaceUrl(space, bookmarkPath(b))} class={`${LINK} min-w-0 truncate`}>
            {nameOf(b)}
          </Link>
        ) : (
          <span class="text-zinc-500 italic">{nameOf(b)}</span>
        )}
        {/* A conversation: what it's about (the space's channel says so in its name). */}
        {b.kind === "conversation" && about !== "space" ? <Badge>{t(`bookmarks.about.${about}`)}</Badge> : null}
      </span>
      <span class="shrink-0 text-xs text-zinc-500" title={formatInstant(b.created_at)}>
        {formatRelative(b.created_at)}
      </span>
      <IconButton
        label={t("bookmarks.remove")}
        size="xs"
        onClick={() => {
          setBookmark(space.id, b, false).then(onRemoved, onRemoved);
        }}
      >
        <CloseIcon />
      </IconButton>
    </li>
  );
}

/** One kind's bookmarks, newest first; items with a way to their list (filtered by them). */
function Group({
  space,
  kind,
  list,
  r,
}: {
  space: SpaceOut;
  kind: BookmarkKind;
  list: Bookmark[];
  r: Resource<Bookmark[]>;
}) {
  return (
    <section aria-labelledby={`bookmarks-${kind}`}>
      <h2 id={`bookmarks-${kind}`} class="mb-2 flex items-baseline gap-3 text-sm font-semibold text-zinc-700">
        {t(`bookmarks.kind.${kind}`)}
        <span class="font-normal text-zinc-500 tabular-nums">{list.length}</span>
        {kind === "item" ? (
          <Link href={`${spaceUrl(space, "/items")}?bookmarked=true`} class={`${LINK} ml-auto text-xs font-normal`}>
            {t("bookmarks.inItems")}
          </Link>
        ) : null}
      </h2>
      <ul class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
        {list.map((b) => (
          <Row key={bookmarkKey(b)} space={space} b={b} onRemoved={r.reload} />
        ))}
      </ul>
    </section>
  );
}

/**
 * Your bookmarks in the space, by kind (items, conversations, comments, changes), newest first, each with a way to
 * remove it.
 */
export function Bookmarks({ space }: PageProps) {
  const r = useResource(`bookmarks:${space.id}`, (signal) =>
    api.get<Bookmark[]>(spaceApi(space, "/bookmarks"), undefined, signal),
  );
  return (
    <>
      <PageHeader title={t("nav.bookmarks")} />
      <Async r={r}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState title={t("bookmarks.empty")}>{t("bookmarks.emptyHint")}</EmptyState>
          ) : (
            <div class="flex max-w-3xl flex-col gap-8">
              {groupBookmarks(list).map((g) => (
                <Group key={g.kind} space={space} kind={g.kind} list={g.list} r={r} />
              ))}
            </div>
          )
        }
      </Async>
    </>
  );
}
